/**
 * Stomach organ texture — brass/gold riveted kettle inspired by the BrainPOP
 * "Guts and Bolts" art. A round brass chamber: the port cone above feeds
 * gruel straight into the top of the dome; the castellated joint at the
 * bottom passes food to the intestines. Rivets line the equator; a small
 * pressure gauge + red indicator sit on the dome; a green-tinted brass
 * spigot is mounted on the left as a flavor detail.
 *
 * 3 cells wide × 3 cells tall = 150 × 150 px.
 */
import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import type { HasTexture } from './core';

export function registerStomachTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-stomach-idle')) makeStomach(scene, 'organ-stomach-idle', false);
  if (!has('organ-stomach-active')) makeStomach(scene, 'organ-stomach-active', true);
}

function makeStomach(scene: Phaser.Scene, key: string, active: boolean): void {
  const cells = 3;
  const size = cells * GRID_SIZE; // 150
  const g = scene.add.graphics();

  const cx = size / 2;
  // Dome occupies roughly the top 2/3 of the sprite. The bottom row (y=100+)
  // is left clear so the castellated output port (centered at dRow=2, y≈125)
  // reads as a set of legs hanging cleanly below the dome — matches the
  // reference art. Cone-on-top still meets the dome's curved top.
  const cy = size * 0.34; // ~51 — dome center in the upper portion

  // ---------- Brass dome (main body) ----------
  const domeW = size * 0.84; // ~126
  const domeH = size * 0.66; // ~99 — squat, more egg-shaped (matches reference)
  const domeColor = active ? 0xe6c050 : 0xd4a93a; // brass / gold
  const domeShadow = active ? 0xa8801c : 0x9a7218;
  const domeOutline = 0x6b4a10;

  // Soft drop shadow
  g.fillStyle(0x000000, 0.22);
  g.fillEllipse(cx + 2, cy + 6, domeW + 4, domeH + 4);

  // Main fill
  g.fillStyle(domeColor, 1);
  g.fillEllipse(cx, cy, domeW, domeH);

  // Bottom shading half-ellipse (darker, gives the dome a 3D sphere look)
  g.fillStyle(domeShadow, 0.55);
  g.fillEllipse(cx, cy + domeH * 0.18, domeW * 0.92, domeH * 0.62);

  // Top highlight (cream gleam on upper-left)
  g.fillStyle(0xfff4c4, 0.55);
  g.fillEllipse(cx - domeW * 0.22, cy - domeH * 0.28, domeW * 0.45, domeH * 0.22);
  // Smaller bright shine
  g.fillStyle(0xffffff, 0.7);
  g.fillEllipse(cx - domeW * 0.28, cy - domeH * 0.30, domeW * 0.18, domeH * 0.08);

  // Outline
  g.lineStyle(2.5, domeOutline, 1);
  g.strokeEllipse(cx, cy, domeW, domeH);

  // ---------- Rim band around the equator ----------
  // A horizontal darker band that visually divides the upper and lower halves
  // of the dome and serves as the anchor for the rivets.
  const rimY = cy - 2;
  const rimH = 14;
  // Clip the band to inside the dome by drawing a rect then a thinner ellipse-band:
  g.fillStyle(0xb88a20, 1);
  g.fillEllipse(cx, rimY, domeW - 4, rimH);
  g.lineStyle(1.5, 0x6b4a10, 1);
  g.strokeEllipse(cx, rimY, domeW - 4, rimH);
  // top edge highlight on the band
  g.fillStyle(0xffe89a, 0.6);
  g.fillEllipse(cx, rimY - 4, domeW - 8, 3);

  // Rivets along the band
  const rivetCount = 8;
  for (let i = 0; i < rivetCount; i++) {
    const t = i / (rivetCount - 1);
    const x = cx - (domeW - 18) / 2 + (domeW - 18) * t;
    // Rivet head
    g.fillStyle(0xfff0b0, 1);
    g.fillCircle(x, rimY, 3);
    g.fillStyle(0x8c6420, 1);
    g.fillCircle(x, rimY, 2);
    g.fillStyle(0xfff4c4, 1);
    g.fillCircle(x - 0.6, rimY - 0.6, 0.8);
  }

  // ---------- Green spigot (left side) ----------
  // A small brass-with-green-patina valve sticking out the left.
  const spigotX = cx - domeW * 0.46;
  const spigotY = cy + 4;
  // Spigot body (horizontal bar)
  g.fillStyle(0x8aa050, 1); // greenish brass
  g.fillRoundedRect(spigotX - 18, spigotY - 4, 18, 8, 2);
  g.lineStyle(1.2, 0x3a4a1a, 1);
  g.strokeRoundedRect(spigotX - 18, spigotY - 4, 18, 8, 2);
  // Vertical valve head
  g.fillStyle(0x8aa050, 1);
  g.fillRoundedRect(spigotX - 14, spigotY - 12, 10, 8, 2);
  g.lineStyle(1.2, 0x3a4a1a, 1);
  g.strokeRoundedRect(spigotX - 14, spigotY - 12, 10, 8, 2);
  // Valve handle (small dark cap on top)
  g.fillStyle(0x4a5a20, 1);
  g.fillCircle(spigotX - 9, spigotY - 14, 3);
  // Spigot highlight
  g.fillStyle(0xc8d890, 0.7);
  g.fillRect(spigotX - 16, spigotY - 3, 12, 1.5);

  // ---------- Pressure-gauge dial (right side) ----------
  const gaugeX = cx + domeW * 0.30;
  const gaugeY = cy + domeH * 0.10;
  const gaugeR = 9;
  g.fillStyle(0xf0e0a0, 1);
  g.fillCircle(gaugeX, gaugeY, gaugeR);
  g.lineStyle(1.5, 0x6b4a10, 1);
  g.strokeCircle(gaugeX, gaugeY, gaugeR);
  // Tick marks
  g.lineStyle(1, 0x3a2810, 1);
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI * (1 - i / 5);
    const x1 = gaugeX + Math.cos(a) * (gaugeR - 1);
    const y1 = gaugeY + Math.sin(a) * (gaugeR - 1);
    const x2 = gaugeX + Math.cos(a) * (gaugeR - 3);
    const y2 = gaugeY + Math.sin(a) * (gaugeR - 3);
    g.beginPath();
    g.moveTo(x1, y1);
    g.lineTo(x2, y2);
    g.strokePath();
  }
  // Needle — points higher when active (in the "green" zone)
  const needleAngle = active ? -Math.PI * 0.25 : -Math.PI * 0.7;
  g.lineStyle(1.5, 0xb01a1a, 1);
  g.beginPath();
  g.moveTo(gaugeX, gaugeY);
  g.lineTo(gaugeX + Math.cos(needleAngle) * (gaugeR - 2), gaugeY + Math.sin(needleAngle) * (gaugeR - 2));
  g.strokePath();
  // Needle pivot
  g.fillStyle(0x3a2810, 1);
  g.fillCircle(gaugeX, gaugeY, 1.5);

  // ---------- Activation glow (subtle red status light when active) ----------
  if (active) {
    g.fillStyle(0xff5a3a, 1);
    g.fillCircle(cx + domeW * 0.18, cy - domeH * 0.18, 3);
    g.fillStyle(0xffd8a8, 0.9);
    g.fillCircle(cx + domeW * 0.18 - 0.8, cy - domeH * 0.18 - 0.8, 1.2);
  } else {
    // Dim red indicator
    g.fillStyle(0x8a2018, 1);
    g.fillCircle(cx + domeW * 0.18, cy - domeH * 0.18, 3);
  }

  g.generateTexture(key, size, size);
  g.destroy();
}
